import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Ban, Bell, Briefcase, CircleDot, Loader2, X } from 'lucide-react';
import { serviceRoleClient } from '../../services/serviceRoleClient';

const PAGE = 40;

/** Jobs the partner app / RSA / WhatsApp / B2B can complete. Not call-center history. */
export const PARTNER_APP_JOB_SOURCES = [
  'customer_rsa_app',
  'customer_rsa_bargain',
  'customer_rsa_on_demand',
  'enterprise_manual',
  'enterprise_api',
  'whatsapp_rsa',
  'whatsapp_ai',
];

const JOB_SELECT =
  'id, service_type, source, pickup_address, pickup_latitude, pickup_longitude, location_link, request_status, complaint_status, assigned_mechanic_id, assigned_mechanic_name, customer_name, contact_number, vehicle_number, problem, requested_at, complaint_registered_at, cancelled_at, updated_at';

const CLOSED_STATUSES = new Set(['completed', 'cancelled', 'canceled', 'closed', 'rejected']);

const statusKey = (job) => {
  const req = String(job?.request_status || '').trim().toLowerCase();
  const comp = String(job?.complaint_status || '').trim().toLowerCase();
  if (CLOSED_STATUSES.has(req)) return req;
  if (CLOSED_STATUSES.has(comp)) return comp;
  return req || comp || '';
};

const isOpenJob = (job) => Boolean(job?.id) && !CLOSED_STATUSES.has(statusKey(job));

const CLOSED_STATUS_CSV = 'completed,cancelled,canceled,closed,rejected';

/** Partner-channel jobs that are still open (not completed / cancelled / closed). */
export const applyOpenJobFilter = (q) =>
  q
    .in('source', PARTNER_APP_JOB_SOURCES)
    .or(`request_status.is.null,request_status.not.in.(${CLOSED_STATUS_CSV})`)
    .or(`complaint_status.is.null,complaint_status.not.in.(${CLOSED_STATUS_CSV})`);

const parseLatLng = (lat, lng) => {
  if (lat == null || lng == null || lat === '' || lng === '') return null;
  const a = Number(lat);
  const b = Number(lng);
  if (!Number.isFinite(a) || !Number.isFinite(b)) return null;
  if (a === 0 && b === 0) return null;
  if (a < -90 || a > 90 || b < -180 || b > 180) return null;
  return [a, b];
};

const coordsFromMapsUrl = (url) => {
  const text = String(url || '');
  const q = text.match(/[?&]q=(-?\d+(?:\.\d+)?),(-?\d+(?:\.\d+)?)/i);
  if (q) return parseLatLng(q[1], q[2]);
  const at = text.match(/@(-?\d+(?:\.\d+)?),(-?\d+(?:\.\d+)?)/);
  if (at) return parseLatLng(at[1], at[2]);
  const pair = text.match(/(-?\d{1,2}\.\d+)\s*,\s*(-?\d{1,3}\.\d+)/);
  if (pair) return parseLatLng(pair[1], pair[2]);
  return null;
};

const haversineKm = (lat1, lng1, lat2, lng2) => {
  const a = parseLatLng(lat1, lng1);
  const b = parseLatLng(lat2, lng2);
  if (!a || !b) return null;
  const toRad = (d) => (d * Math.PI) / 180;
  const dLat = toRad(b[0] - a[0]);
  const dLng = toRad(b[1] - a[1]);
  const s =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(a[0])) * Math.cos(toRad(b[0])) * Math.sin(dLng / 2) ** 2;
  return Math.round(6371 * 2 * Math.atan2(Math.sqrt(s), Math.sqrt(1 - s)) * 10) / 10;
};

const jobPickupCoords = (job) =>
  parseLatLng(job?.pickup_latitude, job?.pickup_longitude) ||
  coordsFromMapsUrl(job?.location_link) ||
  coordsFromMapsUrl(job?.pickup_address);

const distanceKm = (partner, job) => {
  const pickup = jobPickupCoords(job);
  if (!pickup || !partner) return null;
  const km = haversineKm(
    partner.service_center_lat,
    partner.service_center_lng,
    pickup[0],
    pickup[1],
  );
  if (km == null || km > 2500) return null;
  return km;
};

const partnerLabel = (row) =>
  String(row?.shop_name || row?.mechanic_name || row?.code || 'Partner').trim() || 'Partner';

const whenText = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString('en-IN', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
};

async function fetchPartnersByIds(ids) {
  const unique = [...new Set(ids.filter(Boolean).map(String))];
  const map = new Map();
  if (!unique.length) return map;
  for (let i = 0; i < unique.length; i += 80) {
    const chunk = unique.slice(i, i + 80);
    const { data } = await serviceRoleClient
      .from('mechanics')
      .select('id, shop_name, mechanic_name, code, service_center_lat, service_center_lng, owner_user_id')
      .in('id', chunk);
    (data || []).forEach((row) => {
      map.set(String(row.id), row);
      if (row.owner_user_id) map.set(String(row.owner_user_id), row);
    });
  }
  const missing = unique.filter((id) => !map.has(id));
  for (let i = 0; i < missing.length; i += 80) {
    const chunk = missing.slice(i, i + 80);
    const { data } = await serviceRoleClient
      .from('mechanics')
      .select('id, shop_name, mechanic_name, code, service_center_lat, service_center_lng, owner_user_id')
      .in('owner_user_id', chunk);
    (data || []).forEach((row) => {
      map.set(String(row.id), row);
      if (row.owner_user_id) map.set(String(row.owner_user_id), row);
    });
  }
  return map;
}

async function attachPartnerQuotes(items) {
  const requestIds = [...new Set(items.map((row) => row.job?.id).filter(Boolean).map(String))];
  if (!requestIds.length) return items;
  const { data, error } = await serviceRoleClient
    .from('service_request_rate_offers')
    .select('service_request_id, mechanic_id, amount, status, proposed_by, created_at')
    .in('service_request_id', requestIds)
    .order('created_at', { ascending: false });
  if (error) {
    console.warn('partner quotes', error.message);
    return items;
  }
  const latest = new Map();
  (data || []).forEach((offer) => {
    if (String(offer.proposed_by || '').toLowerCase() === 'customer') return;
    const amt = Number(offer.amount);
    if (!Number.isFinite(amt) || amt <= 0) return;
    const key = `${offer.service_request_id}|${offer.mechanic_id || ''}`;
    if (!latest.has(key)) {
      latest.set(key, { amount: amt, status: String(offer.status || '') });
    }
  });
  return items.map((item) => {
    const jobId = item.job?.id;
    if (!jobId) return item;
    const ids = [
      item.mechanicId,
      item.partner?.id,
      item.partner?.owner_user_id,
      item.job?.assigned_mechanic_id,
    ]
      .filter(Boolean)
      .map(String);
    let quote = null;
    for (const id of ids) {
      const hit = latest.get(`${jobId}|${id}`);
      if (hit) {
        quote = hit;
        break;
      }
    }
    return { ...item, quote };
  });
}

const quoteLabel = (quote) => {
  if (!quote || !(Number(quote.amount) > 0)) return '';
  const amt = `₹${Number(quote.amount).toLocaleString('en-IN')}`;
  const st = String(quote.status || '').toLowerCase();
  if (st === 'accepted' || st === 'agreed') return `Accepted ${amt}`;
  if (st === 'denied' || st === 'rejected') return `Quoted ${amt} · denied`;
  return `Quoted ${amt}`;
};

async function fetchJobsByIds(ids) {
  const unique = [...new Set(ids.filter(Boolean).map(String))];
  const map = new Map();
  if (!unique.length) return map;
  for (let i = 0; i < unique.length; i += 40) {
    const chunk = unique.slice(i, i + 40);
    const { data, error } = await serviceRoleClient
      .from('service_requests')
      .select(JOB_SELECT)
      .in('id', chunk);
    if (error) throw new Error(error.message);
    (data || []).forEach((row) => map.set(String(row.id), row));
  }
  return map;
}

async function loadRingPage({ mechanicId, offset }) {
  let q = serviceRoleClient
    .from('partner_job_bell_targets')
    .select('mechanic_id, request_id, bell_started_at', { count: 'exact' })
    .order('bell_started_at', { ascending: false })
    .range(offset, offset + PAGE - 1);
  if (mechanicId) q = q.eq('mechanic_id', mechanicId);
  const { data, error, count } = await q;
  if (error) throw new Error(error.message);
  const rows = data || [];
  const jobs = await fetchJobsByIds(rows.map((r) => r.request_id));
  const partners = await fetchPartnersByIds(rows.map((r) => r.mechanic_id));
  const items = rows.map((row) => ({
    key: `${row.request_id}-${row.mechanic_id}-${row.bell_started_at}`,
    kind: 'ring',
    at: row.bell_started_at,
    mechanicId: row.mechanic_id,
    partner: partners.get(String(row.mechanic_id)) || null,
    job: jobs.get(String(row.request_id)) || null,
  }));
  return {
    total: count ?? offset + rows.length,
    items: await attachPartnerQuotes(items),
  };
}

async function loadCompletedPage({ mechanicId, offset }) {
  let assignIds = [];
  if (mechanicId) {
    const { data: mech } = await serviceRoleClient
      .from('mechanics')
      .select('id, owner_user_id')
      .eq('id', mechanicId)
      .maybeSingle();
    assignIds = [mech?.id, mech?.owner_user_id].filter(Boolean);
  }

  let q = serviceRoleClient
    .from('service_requests')
    .select(JOB_SELECT, { count: 'exact' })
    .in('source', PARTNER_APP_JOB_SOURCES)
    .or(
      'request_status.eq.completed,complaint_status.eq.completed,request_status.eq.closed,complaint_status.eq.closed',
    )
    .not('assigned_mechanic_id', 'is', null)
    .order('updated_at', { ascending: false, nullsFirst: false })
    .range(offset, offset + PAGE - 1);
  if (assignIds.length === 1) q = q.eq('assigned_mechanic_id', assignIds[0]);
  else if (assignIds.length > 1) q = q.in('assigned_mechanic_id', assignIds);

  const { data, error, count } = await q;
  if (error) throw new Error(error.message);
  const rows = data || [];
  const partners = await fetchPartnersByIds(rows.map((r) => r.assigned_mechanic_id));
  const items = rows.map((job) => ({
    key: job.id,
    kind: 'completed',
    at: job.updated_at || job.complaint_registered_at || job.requested_at,
    mechanicId: job.assigned_mechanic_id,
    partner: partners.get(String(job.assigned_mechanic_id)) || null,
    job,
  }));
  return {
    total: count ?? offset + rows.length,
    items: await attachPartnerQuotes(items),
  };
}

async function partnerAssignIds(mechanicId) {
  if (!mechanicId) return [];
  const { data: mech } = await serviceRoleClient
    .from('mechanics')
    .select('id, owner_user_id')
    .eq('id', mechanicId)
    .maybeSingle();
  return [mech?.id, mech?.owner_user_id].filter(Boolean);
}

const applyAssignFilter = (q, assignIds) => {
  if (assignIds.length === 1) return q.eq('assigned_mechanic_id', assignIds[0]);
  if (assignIds.length > 1) return q.in('assigned_mechanic_id', assignIds);
  return q;
};

const CANCEL_NOTE = 'Cancelled by admin from Partner job notifications';

async function cancelRequestById(jobId) {
  try {
    const { data, error: rpcError } = await serviceRoleClient.rpc('admin_cancel_service_request', {
      p_request_id: jobId,
      p_note: CANCEL_NOTE,
    });
    if (rpcError) throw rpcError;
    if (data?.success === false) throw new Error(data.error || 'Cancel failed');
    return true;
  } catch {
    const { error: uErr } = await serviceRoleClient
      .from('service_requests')
      .update({
        request_status: 'cancelled',
        complaint_status: 'cancelled',
        open_for_partner_accept: false,
        cancelled_at: new Date().toISOString(),
        cancelled_remark: CANCEL_NOTE,
        updated_at: new Date().toISOString(),
      })
      .eq('id', jobId);
    if (uErr) throw uErr;
    return true;
  }
}

async function fetchAllOpenJobIds(mechanicId) {
  const assignIds = await partnerAssignIds(mechanicId);
  const ids = [];
  const chunk = 200;
  let offset = 0;
  while (true) {
    let q = applyOpenJobFilter(
      serviceRoleClient
        .from('service_requests')
        .select('id')
        .order('id', { ascending: true })
        .range(offset, offset + chunk - 1),
    );
    q = applyAssignFilter(q, assignIds);
    const { data, error } = await q;
    if (error) throw new Error(error.message);
    const rows = data || [];
    if (!rows.length) break;
    ids.push(...rows.map((row) => row.id).filter(Boolean));
    if (rows.length < chunk) break;
    offset += chunk;
  }
  return [...new Set(ids.map(String))];
}

async function loadOpenPage({ mechanicId, offset }) {
  const assignIds = await partnerAssignIds(mechanicId);

  let q = applyAssignFilter(
    applyOpenJobFilter(
      serviceRoleClient
        .from('service_requests')
        .select(JOB_SELECT, { count: 'exact' })
        .order('requested_at', { ascending: false, nullsFirst: false })
        .range(offset, offset + PAGE - 1),
    ),
    assignIds,
  );

  const { data, error, count } = await q;
  if (error) throw new Error(error.message);
  const rows = data || [];
  const partners = await fetchPartnersByIds(rows.map((r) => r.assigned_mechanic_id));
  const items = rows.map((job) => ({
    key: job.id,
    kind: 'open',
    at: job.requested_at || job.complaint_registered_at || job.updated_at,
    mechanicId: job.assigned_mechanic_id || mechanicId,
    partner: partners.get(String(job.assigned_mechanic_id)) || null,
    job,
  }));
  return {
    total: count ?? offset + rows.length,
    items: await attachPartnerQuotes(items),
  };
}

export default function PartnerActivityFeed({ open, kind, mechanicId, title, onClose, onChanged }) {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [cancelBusyId, setCancelBusyId] = useState(null);
  const [toast, setToast] = useState('');
  const offsetRef = useRef(0);
  const doneRef = useRef(false);
  const busyRef = useRef(false);
  const sentinelRef = useRef(null);
  const listRef = useRef(null);

  const loadMore = useCallback(async () => {
    if (!open || busyRef.current || doneRef.current) return;
    busyRef.current = true;
    setLoading(true);
    setError('');
    try {
      const page =
        kind === 'rings'
          ? await loadRingPage({ mechanicId, offset: offsetRef.current })
          : kind === 'open'
            ? await loadOpenPage({ mechanicId, offset: offsetRef.current })
            : await loadCompletedPage({ mechanicId, offset: offsetRef.current });
      setTotal(page.total);
      setItems((prev) => {
        const seen = new Set(prev.map((row) => row.key));
        return [...prev, ...page.items.filter((row) => !seen.has(row.key))];
      });
      offsetRef.current += page.items.length;
      if (!page.items.length || offsetRef.current >= page.total) doneRef.current = true;
    } catch (e) {
      setError(e.message || 'Could not load details');
      doneRef.current = true;
    } finally {
      busyRef.current = false;
      setLoading(false);
    }
  }, [open, kind, mechanicId]);

  const markCancelled = (jobId) => {
    setItems((prev) => {
      if (kind === 'open') return prev.filter((row) => String(row.job?.id) !== String(jobId));
      return prev.map((row) => {
        if (String(row.job?.id) !== String(jobId)) return row;
        return {
          ...row,
          job: {
            ...row.job,
            request_status: 'cancelled',
            complaint_status: 'cancelled',
            cancelled_at: row.job?.cancelled_at || new Date().toISOString(),
          },
        };
      });
    });
    if (kind === 'open') setTotal((n) => Math.max(0, n - 1));
  };

  const cancelJob = async (job) => {
    if (!job?.id || cancelBusyId) return;
    const name = job.customer_name || job.contact_number || 'this complaint';
    if (!window.confirm(`Cancel open complaint for ${name}?`)) return;
    setCancelBusyId(job.id);
    setToast('');
    try {
      await cancelRequestById(job.id);
      markCancelled(job.id);
      setToast('Complaint cancelled');
      onChanged?.();
    } catch (e) {
      setToast(e?.message || 'Cancel failed');
    } finally {
      setCancelBusyId(null);
    }
  };

  const cancelAllOpen = async () => {
    if (kind !== 'open' || cancelBusyId) return;
    const label = total === 1 ? '1 open case' : `${total.toLocaleString('en-IN')} open cases`;
    if (!window.confirm(`Cancel all ${label}? This cannot be undone.`)) return;
    setCancelBusyId('all');
    setToast('Cancelling all open cases…');
    try {
      const ids = await fetchAllOpenJobIds(mechanicId);
      if (!ids.length) {
        setItems([]);
        setTotal(0);
        setToast('No open cases left');
        onChanged?.();
        return;
      }
      let ok = 0;
      let failed = 0;
      const batch = 6;
      for (let i = 0; i < ids.length; i += batch) {
        const slice = ids.slice(i, i + batch);
        const results = await Promise.allSettled(slice.map((id) => cancelRequestById(id)));
        results.forEach((r) => {
          if (r.status === 'fulfilled') ok += 1;
          else failed += 1;
        });
        setToast(
          failed
            ? `Cancelling… ${ok + failed} / ${ids.length} (${failed} failed)`
            : `Cancelling… ${ok} / ${ids.length}`,
        );
      }
      setItems([]);
      setTotal(0);
      doneRef.current = true;
      setToast(
        failed
          ? `${ok.toLocaleString('en-IN')} cancelled, ${failed} failed`
          : `${ok.toLocaleString('en-IN')} complaints cancelled`,
      );
      onChanged?.();
    } catch (e) {
      setToast(e?.message || 'Cancel all failed');
    } finally {
      setCancelBusyId(null);
    }
  };

  useEffect(() => {
    if (!open) return undefined;
    offsetRef.current = 0;
    doneRef.current = false;
    busyRef.current = false;
    setItems([]);
    setTotal(0);
    setError('');
    setCancelBusyId(null);
    setToast('');
    loadMore();
    return undefined;
  }, [open, kind, mechanicId, loadMore]);

  useEffect(() => {
    if (!open) return undefined;
    const el = sentinelRef.current;
    if (!el) return undefined;
    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) loadMore();
      },
      { root: listRef.current, rootMargin: '160px' },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [open, loadMore, items.length]);

  if (!open) return null;

  const Icon = kind === 'rings' ? Bell : kind === 'open' ? CircleDot : Briefcase;

  const openComplaint = (job) => {
    const id = job?.id;
    if (!id) return;
    navigate(`/service_advisor/complaints/${id}`);
  };

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center p-3 sm:p-5">
      <button type="button" className="absolute inset-0 bg-slate-900/50" aria-label="Close" onClick={onClose} />
      <div className="relative z-10 flex max-h-[88vh] w-full max-w-2xl flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-2xl">
        <div className="flex items-start justify-between gap-3 border-b border-slate-100 px-4 py-3">
          <div>
            <p className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-wide text-indigo-600">
              <Icon size={13} />
              {kind === 'rings' ? 'Job notifications' : kind === 'open' ? 'Open cases' : 'Completed cases'}
            </p>
            <h3 className="text-base font-semibold text-slate-900">{title}</h3>
            <p className="text-xs text-slate-500">
              {total.toLocaleString('en-IN')} total · scroll to load more
            </p>
          </div>
          <div className="flex items-center gap-2">
            {kind === 'open' && total > 0 ? (
              <button
                type="button"
                disabled={Boolean(cancelBusyId)}
                onClick={cancelAllOpen}
                className="inline-flex items-center gap-1 rounded-md border border-red-200 bg-red-50 px-2.5 py-1.5 text-xs font-semibold text-red-700 hover:bg-red-100 disabled:opacity-50"
                title="Cancel every open case in this list"
              >
                <Ban size={13} />
                {cancelBusyId === 'all' ? 'Cancelling…' : 'Cancel all'}
              </button>
            ) : null}
            <button
              type="button"
              onClick={onClose}
              className="rounded-lg p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-700"
            >
              <X size={18} />
            </button>
          </div>
        </div>

        {toast ? (
          <p
            className={`mx-4 mt-3 rounded-lg px-3 py-2 text-xs font-semibold ${
              /fail|error/i.test(toast) ? 'bg-rose-50 text-rose-700' : 'bg-emerald-50 text-emerald-800'
            }`}
          >
            {toast}
          </p>
        ) : null}

        <div ref={listRef} className="min-h-0 flex-1 overflow-y-auto px-4 py-2">
          {items.map((row) => {
            const job = row.job;
            const svc = String(job?.service_type || job?.problem || '').trim() || 'Service not recorded';
            const addr =
              String(job?.pickup_address || job?.location_link || '').trim() || 'No pickup address';
            const km = distanceKm(row.partner, job);
            const status = job?.request_status || job?.complaint_status || '';
            const source = String(job?.source || '').trim();
            const open = isOpenJob(job);
            return (
              <div key={row.key} className="border-b border-slate-100 py-3">
                <div className="flex items-start justify-between gap-3">
                  <button
                    type="button"
                    disabled={!job?.id}
                    onClick={() => openComplaint(job)}
                    className="min-w-0 flex-1 rounded-lg px-1 py-0.5 text-left hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-indigo-200 disabled:cursor-default"
                    title="Open complaint details"
                  >
                    <p className="text-sm font-semibold text-slate-900">{svc}</p>
                    <p className="mt-0.5 text-xs text-slate-600">{addr}</p>
                    {km != null ? (
                      <p className="mt-0.5 text-xs font-semibold text-indigo-700">{km} km from shop</p>
                    ) : null}
                    {quoteLabel(row.quote) ? (
                      <p className="mt-0.5 text-xs font-bold text-amber-700">{quoteLabel(row.quote)}</p>
                    ) : null}
                    <p className="mt-1 text-[11px] text-slate-500">
                      {whenText(row.at)}
                      {row.partner ? ` · ${partnerLabel(row.partner)}` : ''}
                      {job?.assigned_mechanic_name && !row.partner
                        ? ` · ${job.assigned_mechanic_name}`
                        : ''}
                      {status ? ` · ${status}` : ''}
                      {source ? ` · ${source}` : ''}
                    </p>
                    {job?.customer_name || job?.vehicle_number ? (
                      <p className="text-[11px] text-slate-400">
                        {[job.customer_name, job.vehicle_number].filter(Boolean).join(' · ')}
                      </p>
                    ) : null}
                  </button>
                  {open ? (
                    <button
                      type="button"
                      disabled={Boolean(cancelBusyId)}
                      onClick={(e) => {
                        e.stopPropagation();
                        cancelJob(job);
                      }}
                      className="inline-flex shrink-0 items-center gap-1 rounded-md border border-red-200 bg-red-50 px-2 py-1 text-xs font-medium text-red-700 hover:bg-red-100 disabled:opacity-50"
                      title="Cancel open complaint"
                    >
                      <Ban size={13} />
                      {cancelBusyId === job.id ? '…' : 'Cancel'}
                    </button>
                  ) : null}
                </div>
              </div>
            );
          })}
          {error ? <p className="py-6 text-center text-sm text-rose-600">{error}</p> : null}
          {!error && !loading && items.length === 0 ? (
            <p className="py-10 text-center text-sm text-slate-500">No records yet</p>
          ) : null}
          <div ref={sentinelRef} className="h-8" />
          {loading ? (
            <div className="flex items-center justify-center gap-2 py-4 text-sm text-slate-500">
              <Loader2 size={16} className="animate-spin" />
              Loading…
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}

export async function countExact(table, apply, selectCol = 'id') {
  let q = serviceRoleClient.from(table).select(selectCol, { count: 'exact', head: true });
  if (apply) q = apply(q);
  const { count, error } = await q;
  if (error) return 0;
  return Number(count || 0);
}
